import { useMemo } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { ExtendedAppointment } from '@frontend/schedule-helpers';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { ScheduleRequestList } from '../../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestList';
import { AppointmentList } from './appointment-list';
import { PanelHeader } from './panel-header';

const getAppointmentsRangeText = (selectedAppointments: ExtendedAppointment[]) => {
  if (!selectedAppointments.length) return '';

  const appointmentTimes = selectedAppointments
    .filter((appointment) => appointment.start && appointment.duration)
    .map((appointment) => {
      const startTime = dayjs(appointment.start);
      const endTime = startTime.add(appointment.duration || 0, 'minute');
      return { start: startTime, end: endTime };
    });

  if (appointmentTimes.length === 0) return '';

  const minStartTime = appointmentTimes.reduce((min, current) =>
    current.start.isBefore(min.start) ? current : min
  ).start;

  const maxEndTime = appointmentTimes.reduce((max, current) => (current.end.isAfter(max.end) ? current : max)).end;

  return `${minStartTime.format('h:mm A')} - ${maxEndTime.format('h:mm A')}`;
};

export const OverlappingAppointmentsPanel = () => {
  const { t } = useTranslation('schedule');
  const { selectedAppointments: appointments, selectedRequests } = useScheduleAppointmentInfoPanelShallowStore(
    'selectedAppointments',
    'selectedRequests'
  );

  const providerName = appointments[0]?.practitionerName ?? selectedRequests[0]?.practitionerName ?? '';
  const scheduleRequests = selectedRequests as ScheduleRequest[];

  const appointmentTimeRange = useMemo(() => getAppointmentsRangeText(appointments), [appointments]);
  const appointmentInfoText = t('{{count}} appointments • {{timeRange}}', {
    count: appointments.length,
    timeRange: appointmentTimeRange,
  });
  const scheduleRequestInfoText = t('{{count}} requests', { count: scheduleRequests.length });
  const hasAppointments = appointments.length > 0;

  return (
    <section css={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {hasAppointments && (
        <>
          <PanelHeader
            title={t('Overlapping Appointments')}
            appointmentsInfoText={appointmentInfoText}
            providerName={providerName}
          />
          <AppointmentList appointments={appointments} />
        </>
      )}
      {scheduleRequests.length > 0 && (
        <>
          <PanelHeader
            title={!hasAppointments ? t('Overlapping Requests') : ''}
            appointmentsInfoText={scheduleRequestInfoText}
            providerName={!hasAppointments ? providerName : ''}
          />
          <ScheduleRequestList scheduleRequests={scheduleRequests} />
        </>
      )}
    </section>
  );
};
